import { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { useSensor, useSensors, MouseSensor, DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { IColumn } from '../interfaces';
import { getDraggableColumnNames } from '../helpers';

interface IUseColumnDragAndDropParams {
  columns: IColumn[],
  isHeaderDraggable: boolean,
  initialColumnOrder?: string[],
  onColumnOrderChange?: (newOrder: string[]) => void,
  tableRef: React.RefObject<HTMLTableElement | null>,
}

const useColumnDragAndDrop = ({
  columns,
  isHeaderDraggable,
  initialColumnOrder,
  onColumnOrderChange,
  tableRef,
}: IUseColumnDragAndDropParams) => {
  const [columnOrder, setColumnOrder] = useState<string[]>(
    () => initialColumnOrder || getDraggableColumnNames(columns),
  );
  const [activeColumnName, setActiveColumnName] = useState<string | null>(null);
  const cellRectsRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    setColumnOrder(initialColumnOrder || getDraggableColumnNames(columns));
  }, [columns.length]);

  const orderedColumns = useMemo<IColumn[]>(() => {
    if (!isHeaderDraggable) {
      return columns;
    }

    const result: IColumn[] = [];
    const draggableOrdered = columnOrder
      .map((name) => columns.find((col) => col.name === name))
      .filter(Boolean) as IColumn[];
    let draggableIdx = 0;

    columns.forEach((col) => {
      if (col.isDraggable === false) {
        result.push(col);
      } else if (draggableIdx < draggableOrdered.length) {
        result.push(draggableOrdered[draggableIdx]);
        draggableIdx++;
      }
    });

    return result;
  }, [isHeaderDraggable, columnOrder, columns]);

  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 5 } }));

  const onDragStart = useCallback(({ active }: DragStartEvent): void => {
    setActiveColumnName(active.id as string);
  }, []);

  const onDragOver = useCallback(({ active, over }: DragOverEvent): void => {
    if (!over || active.id === over.id) {
      return;
    }

    if (tableRef.current) {
      const cells = tableRef.current.querySelectorAll('tbody td');
      const rects = new Map<string, number>();
      cells.forEach((cell: Element) => {
        const key = `${ (cell as HTMLElement).dataset.test }-${ cell.closest('tr')?.dataset.row }`;
        rects.set(key, cell.getBoundingClientRect().left);
      });
      cellRectsRef.current = rects;
    }

    setColumnOrder((prev) => {
      const oldIndex = prev.indexOf(active.id as string);
      const newIndex = prev.indexOf(over.id as string);
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const onDragEnd = useCallback(({ active, over }: DragEndEvent): void => {
    setActiveColumnName(null);

    if (!over || active.id === over.id) {
      return;
    }

    setColumnOrder((prev) => {
      onColumnOrderChange?.(prev);
      return prev;
    });
  }, [onColumnOrderChange]);

  // FLIP animation: after reorder, animate cells from old position to new
  useLayoutEffect(() => {
    if (!tableRef.current || cellRectsRef.current.size === 0) {
      return;
    }

    const cells = tableRef.current.querySelectorAll('tbody td');
    const prevRects = cellRectsRef.current;

    cells.forEach((cell: Element) => {
      const el = cell as HTMLElement;
      const key = `${ el.dataset.test }-${ el.closest('tr')?.dataset.row }`;
      const prevLeft = prevRects.get(key);

      if (prevLeft === undefined) {
        return;
      }

      const newLeft = el.getBoundingClientRect().left;
      const delta = prevLeft - newLeft;

      if (delta === 0) {
        return;
      }

      el.style.transform = `translateX(${ delta }px)`;
      el.style.transition = 'none';

      requestAnimationFrame(() => {
        el.style.transition = 'transform 200ms ease';
        el.style.transform = '';
      });
    });

    cellRectsRef.current = new Map();
  }, [columnOrder]);

  return {
    orderedColumns,
    activeColumnName,
    sensors,
    onDragStart,
    onDragOver,
    onDragEnd,
  };
};

export default useColumnDragAndDrop;
